import React from 'react';
import { useWindowDimensions } from 'react-native';
import { Check, X } from 'lucide-react-native';
import { StyleSheet } from 'react-native-unistyles';
import { buildSpreadScoreAxisMarks, buildSpreadScoreBars, type SpreadBarResult, type SpreadScoreBarModel } from '@shared/spreadScoreBars';
import { getTeamPrimaryColor } from '../lib/teamColors';
import { Box, Text } from './ui/primitives';
import { tokens } from '../../design-tokens';

const scoreboardSuccess = '#88D9AA';
const scoreboardFailure = '#FF958C';

export type GameScoreboardPickState = 'correct' | 'incorrect' | 'in-progress';

export interface GameScoreboardSpreadPick {
  teamLabel: string;
  line: number;
  side: 'away' | 'home';
}

export interface GameCardScoreboardProps {
  awayScore?: number;
  awayLabel?: string;
  awayLineScores?: unknown;
  awayColor?: string | null;
  homeScore?: number;
  homeLabel?: string;
  homeLineScores?: unknown;
  homeColor?: string | null;
  isCompleted: boolean;
  pickState?: GameScoreboardPickState | null;
  spreadPick?: GameScoreboardSpreadPick | null;
  network?: string | null;
  period?: string | null;
  clock?: string | null;
  situation?: string | null;
  variant?: 'picks' | 'deprecated-led';
  showPeriodScores?: boolean;
}

const ledDigitPatterns: Record<string, string[]> = {
  '0': ['11111', '10001', '10001', '10001', '10001', '10001', '11111'],
  '1': ['00100', '01100', '00100', '00100', '00100', '00100', '01110'],
  '2': ['11111', '00001', '00001', '11111', '10000', '10000', '11111'],
  '3': ['11111', '00001', '00001', '11111', '00001', '00001', '11111'],
  '4': ['10001', '10001', '10001', '11111', '00001', '00001', '00001'],
  '5': ['11111', '10000', '10000', '11111', '00001', '00001', '11111'],
  '6': ['11111', '10000', '10000', '11111', '10001', '10001', '11111'],
  '7': ['11111', '00001', '00001', '00010', '00100', '00100', '00100'],
  '8': ['11111', '10001', '10001', '11111', '10001', '10001', '11111'],
  '9': ['11111', '10001', '10001', '11111', '00001', '00001', '11111'],
};

const styles = StyleSheet.create((theme) => ({
  centerMark: {
    color: tokens.colors.ink.paper,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 32,
    paddingHorizontal: 6,
  },
  footer: {
    alignItems: 'center',
    borderTopColor: tokens.colors.ink.paper,
    borderTopWidth: 1,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    paddingVertical: 4,
  },
  footerDivider: {
    backgroundColor: tokens.colors.neutral[400],
    height: 18,
    width: 1,
  },
  header: {
    alignItems: 'center',
    borderBottomColor: tokens.colors.ink.paper,
    borderBottomWidth: 1,
    flexDirection: 'row',
  },
  headerLabel: {
    color: tokens.colors.ink.paper,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 0.8,
    lineHeight: 20,
  },
  headerSide: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 4,
  },
  headerSideAway: {
    borderRightColor: tokens.colors.ink.paper,
    borderRightWidth: 1,
  },
  ledDigit: {
    backgroundColor: '#171918',
    height: 44,
    justifyContent: 'space-between',
    width: 32,
  },
  ledDot: {
    backgroundColor: '#3A403D',
    borderRadius: 6,
    height: 4,
    width: 4,
  },
  ledDotActive: {
    backgroundColor: '#E33C35',
    shadowColor: '#FF5A50',
    shadowOpacity: 0.8,
    shadowRadius: 2,
  },
  ledDotWinning: {
    backgroundColor: '#FF6B61',
  },
  ledFallback: {
    color: '#E33C35',
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 32,
    fontWeight: '800',
    lineHeight: 40,
  },
  ledRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  ledScore: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6,
    justifyContent: 'center',
    minHeight: 44,
  },
  pickState: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  pickStateLabel: {
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
  },
  pickStateLabelCorrect: {
    color: scoreboardSuccess,
  },
  pickStateLabelIncorrect: {
    color: scoreboardFailure,
  },
  picksBoard: {
    gap: 6,
    marginTop: 6,
  },
  picksBoardCompactSpread: {
    gap: 4,
    marginTop: 0,
  },
  picksClockChip: {
    backgroundColor: theme.colors.neutral100,
    borderRadius: 16,
    paddingHorizontal: 11,
    paddingVertical: 5,
  },
  picksClockText: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
  },
  picksDivider: {
    backgroundColor: theme.colors.neutral200,
    height: 40,
    width: 1,
  },
  picksHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 7,
    minHeight: 24,
  },
  picksNetwork: {
    color: theme.colors.textSecondary,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
    marginLeft: 'auto',
  },
  picksResult: {
    alignItems: 'center',
    borderTopColor: theme.colors.neutral200,
    borderTopWidth: 1,
    flexDirection: 'row',
    gap: 6,
    paddingTop: 10,
  },
  picksResultText: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  picksScore: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 44,
    lineHeight: 48,
  },
  picksScoreRow: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: 48,
  },
  picksScoreTrailing: {
    color: theme.colors.textSecondary,
  },
  picksSituation: {
    alignItems: 'center',
    backgroundColor: theme.colors.neutral100,
    borderRadius: 6,
    justifyContent: 'center',
    minHeight: 30,
    paddingHorizontal: 8,
  },
  picksSituationText: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.bodyMedium,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  picksStatusChip: {
    alignItems: 'center',
    backgroundColor: '#CB1730',
    borderRadius: 16,
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  picksStatusChipFinal: {
    backgroundColor: theme.colors.neutral100,
  },
  picksStatusDot: {
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    height: 7,
    width: 7,
  },
  picksStatusText: {
    color: theme.colors.white,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.7,
  },
  picksStatusTextFinal: {
    color: theme.colors.text,
  },
  picksTeam: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
  scoreRow: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  scoreboard: {
    backgroundColor: '#202322',
    borderColor: tokens.colors.ink.paper,
    borderRadius: 4,
    borderWidth: 2,
    marginTop: 4,
    overflow: 'hidden',
    position: 'relative',
  },
  spreadBarAxis: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 8,
    marginTop: 2,
    minHeight: 16,
  },
  spreadBarAxisGutter: {
    width: 36,
  },
  spreadBarAxisLabel: {
    color: theme.colors.textSecondary,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.2,
    lineHeight: 11,
  },
  spreadBarAxisMark: {
    overflow: 'visible',
    position: 'absolute',
    top: 0,
    width: 40,
  },
  spreadBarAxisMarkAlignCenter: {
    alignItems: 'center',
    marginLeft: -20,
  },
  spreadBarAxisMarkAlignEnd: {
    alignItems: 'flex-end',
    marginLeft: -40,
  },
  spreadBarAxisMarkAlignStart: {
    alignItems: 'flex-start',
  },
  spreadBarAxisMarkAt: (percent: number) => ({
    left: `${percent}%` as const,
  }),
  spreadBarAxisResult: {
    width: 22,
  },
  spreadBarAxisTick: {
    backgroundColor: theme.colors.neutral400,
    height: 4,
    width: 1,
  },
  spreadBarAxisTrack: {
    flex: 1,
    minWidth: 0,
    overflow: 'visible',
    position: 'relative',
  },
  spreadBarFill: {
    alignItems: 'stretch',
    flexDirection: 'row',
    height: '100%',
    minWidth: 0,
  },
  spreadBarFillWidth: (widthPercent: number) => ({
    width: `${widthPercent}%` as const,
  }),
  spreadBarHandicap: {
    alignItems: 'center',
    backgroundColor: '#C5C9CE',
    flexBasis: 0,
    flexShrink: 0,
    height: '100%',
    justifyContent: 'center',
    minWidth: 0,
    overflow: 'hidden',
  },
  spreadBarHandicapGrow: (flexGrow: number) => ({
    flexGrow,
  }),
  spreadBarHandicapText: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.2,
  },
  spreadBarPoints: {
    flexBasis: 0,
    flexShrink: 0,
    height: '100%',
    minWidth: 0,
  },
  spreadBarPointsFill: (flexGrow: number, backgroundColor: string) => ({
    backgroundColor,
    flexGrow,
  }),
  spreadBarResult: {
    alignItems: 'center',
    height: 22,
    justifyContent: 'center',
    width: 22,
  },
  spreadBarRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    minHeight: 22,
  },
  spreadBarSeam: {
    alignSelf: 'stretch',
    backgroundColor: theme.colors.paper,
    width: 2,
  },
  spreadBarTeam: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.4,
    width: 36,
  },
  spreadBarTrack: {
    backgroundColor: theme.colors.neutral100,
    flex: 1,
    flexDirection: 'row',
    height: 14,
    minWidth: 0,
    overflow: 'hidden',
  },
  spreadBars: {
    gap: 6,
    marginTop: 8,
  },
  spreadBarsCompact: {
    gap: 3,
    marginTop: 1,
  },
  spreadDetail: {
    alignItems: 'center',
    borderTopColor: '#505A53',
    borderTopWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    justifyContent: 'space-between',
    minHeight: 36,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  spreadImpact: {
    backgroundColor: theme.colors.errorBackground,
    borderRadius: 8,
    gap: 7,
    padding: 9,
  },
  spreadImpactCompact: {
    gap: 2,
    padding: 2,
  },
  spreadImpactDetail: {
    color: theme.colors.textSecondary,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 10,
    lineHeight: 13,
  },
  spreadImpactHeadline: {
    color: theme.colors.error,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 17,
    lineHeight: 20,
  },
  spreadImpactHeadlineNeutral: {
    color: theme.colors.text,
  },
  spreadImpactHeadlinePositive: {
    color: theme.colors.success,
  },
  spreadImpactNeutral: {
    backgroundColor: theme.colors.neutral100,
  },
  spreadImpactPick: {
    color: theme.colors.text,
    flex: 0.42,
    fontFamily: tokens.typography.fontFamily.display,
    fontSize: 19,
    lineHeight: 23,
  },
  spreadImpactPositive: {
    backgroundColor: theme.colors.successBackground,
  },
  spreadImpactReadout: {
    borderLeftColor: theme.colors.neutral300,
    borderLeftWidth: 1,
    flex: 0.58,
    gap: 2,
    minWidth: 0,
    paddingLeft: 12,
  },
  spreadImpactSubhead: {
    color: theme.colors.text,
    fontFamily: tokens.typography.fontFamily.bodyMedium,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 15,
  },
  spreadImpactTop: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: 60,
  },
  spreadImpactTopCompact: {
    minHeight: 36,
  },
  spreadPickLabel: {
    color: tokens.colors.ink.paper,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.4,
  },
  spreadResult: {
    color: tokens.colors.ink.paper,
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.5,
    textAlign: 'right',
  },
  spreadResultNegative: {
    color: scoreboardFailure,
  },
  spreadResultNeutral: {
    color: tokens.colors.warning[300],
  },
  spreadResultPositive: {
    color: scoreboardSuccess,
  },
  statCell: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  statCellDivider: {
    borderRightColor: tokens.colors.neutral[500],
    borderRightWidth: 1,
  },
  statLabel: {
    color: '#AEB8B0',
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 1,
  },
  statPair: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: 2,
  },
  statSeparator: {
    color: tokens.colors.neutral[300],
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    marginHorizontal: 3,
  },
  statValue: {
    color: '#F4F3EC',
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '800',
  },
  stats: {
    borderTopColor: tokens.colors.ink.paper,
    borderTopWidth: 1,
    flexDirection: 'row',
    minHeight: 38,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  status: {
    color: tokens.colors.warning[300],
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.8,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  team: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
}));

function normalizeLineScores(value: unknown): (number | null)[] {
  if (!Array.isArray(value)) return [];
  return value.map((score) => (typeof score === 'number' && Number.isFinite(score) ? score : null));
}

function formatSpreadNumber(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatSigned(value: number): string {
  return `${value > 0 ? '+' : ''}${formatSpreadNumber(value)}`;
}

function getSpreadReadout(
  spreadPick: GameScoreboardSpreadPick | null | undefined,
  awayScore: number | undefined,
  homeScore: number | undefined,
  isCompleted: boolean,
) {
  if (!spreadPick || !Number.isFinite(spreadPick.line)
    || typeof awayScore !== 'number' || typeof homeScore !== 'number') return null;

  const selectedScore = spreadPick.side === 'away' ? awayScore : homeScore;
  const opposingScore = spreadPick.side === 'away' ? homeScore : awayScore;
  const currentMargin = selectedScore - opposingScore;
  const coverMargin = currentMargin + spreadPick.line;
  const coverAt = Math.floor(-spreadPick.line) + 1;
  const pickLabel = `${spreadPick.teamLabel} ${formatSigned(spreadPick.line)}`;
  const fullDetail = `${isCompleted ? 'Final' : 'Current'} margin ${formatSigned(currentMargin)} · cover line ${formatSigned(coverAt)}`;

  if (coverMargin > 0) {
    return {
      pickLabel,
      headline: isCompleted ? 'COVERED' : 'COVERING ATS',
      subhead: `Covering by ${formatSpreadNumber(coverMargin)}`,
      result: `${isCompleted ? 'COVERED' : 'COVERING'} BY ${formatSpreadNumber(coverMargin)}`,
      currentMargin,
      coverAt,
      fullDetail,
      tone: 'positive' as const,
    };
  }
  if (coverMargin === 0) {
    return {
      pickLabel,
      headline: isCompleted ? 'PUSH' : 'AT THE NUMBER',
      subhead: isCompleted ? 'The spread pushed' : 'Need +1 to cover',
      result: isCompleted ? 'PUSH' : 'PUSHING · 1 PT TO COVER',
      currentMargin,
      coverAt,
      fullDetail,
      tone: 'neutral' as const,
    };
  }
  const pointsToCover = Math.max(1, coverAt - currentMargin);
  return {
    pickLabel,
    headline: isCompleted ? "DIDN'T COVER" : 'TRAILING ATS',
    subhead: isCompleted
      ? `Missed by ${formatSpreadNumber(-coverMargin)}`
      : `Need +${pointsToCover} to cover`,
    result: isCompleted
      ? `MISSED BY ${formatSpreadNumber(-coverMargin)}`
      : `${pointsToCover} ${pointsToCover === 1 ? 'PT' : 'PTS'} TO COVER`,
    currentMargin,
    coverAt,
    fullDetail,
    tone: 'negative' as const,
  };
}

function SpreadScoreBarRow({
  bar,
  scale,
  color,
  result,
}: {
  bar: SpreadScoreBarModel;
  scale: number;
  color: string;
  result: SpreadBarResult;
}) {
  const [handicapWidth, setHandicapWidth] = React.useState(0);
  const fillPercent = scale > 0 ? (bar.fill / scale) * 100 : 0;
  const showHandicapLabel = handicapWidth >= 28;
  const resultTestId = result === 'covering'
    ? 'spread-bar-result-correct'
    : result === 'trailing'
      ? 'spread-bar-result-incorrect'
      : 'spread-bar-result-push';

  return (
    <Box style={styles.spreadBarRow} testID={`spread-score-bar-${bar.side}`}>
      <Text style={styles.spreadBarTeam} numberOfLines={1}>{bar.label}</Text>
      <Box style={styles.spreadBarTrack} testID={`spread-bar-track-${bar.side}`}>
        {bar.fill > 0 ? (
          <Box
            testID={`spread-bar-fill-${bar.side}`}
            style={[styles.spreadBarFill, styles.spreadBarFillWidth(fillPercent)]}
          >
            {bar.spreadBoost > 0 ? (
              <Box
                testID={`spread-bar-handicap-${bar.side}`}
                onLayout={(event) => setHandicapWidth(Math.round(event.nativeEvent.layout.width))}
                style={[styles.spreadBarHandicap, styles.spreadBarHandicapGrow(bar.spreadBoost)]}
              >
                {showHandicapLabel ? (
                  <Text style={styles.spreadBarHandicapText}>{formatSigned(bar.spreadBoost)}</Text>
                ) : null}
              </Box>
            ) : null}
            {bar.spreadBoost > 0 && bar.score > 0 ? <Box style={styles.spreadBarSeam} /> : null}
            {bar.score > 0 ? (
              <Box
                testID={`spread-bar-points-${bar.side}`}
                style={[styles.spreadBarPoints, styles.spreadBarPointsFill(bar.score, color)]}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Box style={styles.spreadBarResult}>
        {bar.isPicked && result === 'covering' ? (
          <Box testID={resultTestId} accessibilityLabel="Covering the spread">
            <Check size={16} color={tokens.colors.state.success} strokeWidth={3} />
          </Box>
        ) : null}
        {bar.isPicked && result === 'trailing' ? (
          <Box testID={resultTestId} accessibilityLabel="Not covering the spread">
            <X size={16} color={tokens.colors.state.error} strokeWidth={3} />
          </Box>
        ) : null}
        {bar.isPicked && result === 'push' ? (
          <Box testID={resultTestId} accessibilityLabel="Even against the spread" />
        ) : null}
      </Box>
    </Box>
  );
}

function SpreadScoreAxis({
  away,
  home,
  scale,
}: {
  away: SpreadScoreBarModel;
  home: SpreadScoreBarModel;
  scale: number;
}) {
  const marks = buildSpreadScoreAxisMarks(away, home, scale);
  return (
    <Box style={styles.spreadBarAxis} testID="spread-bar-axis">
      <Box style={styles.spreadBarAxisGutter} />
      <Box style={styles.spreadBarAxisTrack}>
        {marks.map((mark) => (
          <Box
            key={mark.key}
            testID={`spread-bar-axis-${mark.key}`}
            style={[
              styles.spreadBarAxisMark,
              styles.spreadBarAxisMarkAt(mark.percent),
              mark.align === 'end' ? styles.spreadBarAxisMarkAlignEnd
                : mark.align === 'start' ? styles.spreadBarAxisMarkAlignStart
                  : styles.spreadBarAxisMarkAlignCenter,
            ]}
          >
            <Box style={styles.spreadBarAxisTick} />
            <Text style={styles.spreadBarAxisLabel} numberOfLines={1}>{mark.label}</Text>
          </Box>
        ))}
      </Box>
      <Box style={styles.spreadBarAxisResult} />
    </Box>
  );
}

/** Scores only — team logos/names live on the pick slabs above this strip. */
function PicksScore({
  label,
  score,
  isTrailing,
}: {
  label?: string;
  score?: number;
  isTrailing: boolean;
}) {
  return (
    <Box
      style={styles.picksTeam}
      accessibilityLabel={`${label ?? 'Team'} ${score ?? 'no score'}`}
    >
      <Text style={[styles.picksScore, isTrailing && styles.picksScoreTrailing]}>
        {score ?? '—'}
      </Text>
    </Box>
  );
}

const LedScore = React.memo(function LedScore({
  score,
  isWinning,
}: {
  score?: number;
  isWinning: boolean;
}) {
  const value = typeof score === 'number' ? String(score) : '—';

  return (
    <Box style={styles.ledScore} accessibilityLabel={`Score ${value}`}>
      {Array.from(value).map((character, characterIndex) => {
        const pattern = ledDigitPatterns[character];
        if (!pattern) {
          return <Text key={`${character}-${characterIndex}`} style={styles.ledFallback}>{character}</Text>;
        }

        return (
          <Box key={`${character}-${characterIndex}`} style={styles.ledDigit}>
            {pattern.map((row, rowIndex) => (
              <Box key={rowIndex} style={styles.ledRow}>
                {Array.from(row).map((isLit, columnIndex) => (
                  <Box
                    key={`${rowIndex}-${columnIndex}`}
                    style={[
                      styles.ledDot,
                      isLit === '1' ? styles.ledDotActive : undefined,
                      isLit === '1' && isWinning ? styles.ledDotWinning : undefined,
                    ]}
                  />
                ))}
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
});

export const GameCardScoreboard = React.memo(function GameCardScoreboard({
  awayScore,
  awayLabel,
  awayLineScores,
  awayColor,
  homeScore,
  homeLabel,
  homeLineScores,
  homeColor,
  isCompleted,
  pickState,
  spreadPick,
  network,
  period,
  clock,
  situation,
  variant = 'picks',
  showPeriodScores = true,
}: GameCardScoreboardProps) {
  const { width: windowWidth } = useWindowDimensions();
  const awayIsWinning = typeof awayScore === 'number' && typeof homeScore === 'number' && awayScore > homeScore;
  const homeIsWinning = typeof awayScore === 'number' && typeof homeScore === 'number' && homeScore > awayScore;
  const awayPeriods = normalizeLineScores(awayLineScores);
  const homePeriods = normalizeLineScores(homeLineScores);
  const overtimeAvailable = typeof awayPeriods[4] === 'number' || typeof homePeriods[4] === 'number';
  const periodLabels = overtimeAvailable ? ['Q1', 'Q2', 'Q3', 'Q4', 'OT'] : ['Q1', 'Q2', 'Q3', 'Q4'];
  const hasPeriodScores = showPeriodScores && periodLabels.some((_, index) => (
    typeof awayPeriods[index] === 'number' || typeof homePeriods[index] === 'number'
  ));
  const pickStateLabel = pickState === 'correct' ? 'CORRECT' : pickState === 'incorrect' ? 'INCORRECT' : null;
  const PickStateIcon = pickState === 'correct' ? Check : pickState === 'incorrect' ? X : null;
  const spreadReadout = getSpreadReadout(spreadPick, awayScore, homeScore, isCompleted);
  const isCompactSpread = Boolean(spreadReadout && windowWidth < 520);
  const spreadBars = spreadReadout && spreadPick && typeof awayScore === 'number' && typeof homeScore === 'number'
    ? buildSpreadScoreBars({
      awayLabel: awayLabel ?? 'AWAY',
      homeLabel: homeLabel ?? 'HOME',
      awayScore,
      homeScore,
      pickedSide: spreadPick.side,
      pickedLine: spreadPick.line,
      coverTone: spreadReadout.tone,
    })
    : null;
  const awayBarColor = awayColor ?? getTeamPrimaryColor(awayLabel) ?? tokens.colors.neutral[700];
  const homeBarColor = homeColor ?? getTeamPrimaryColor(homeLabel) ?? tokens.colors.neutral[700];

  if (variant === 'picks') {
    const scoreSummary = [
      awayLabel,
      awayScore ?? '—',
      homeLabel,
      homeScore ?? '—',
    ].join(' ');
    return (
      <Box
        style={[styles.picksBoard, isCompactSpread && styles.picksBoardCompactSpread]}
        accessibilityLabel={`Game scoreboard, ${isCompleted ? 'final' : 'live'}, ${scoreSummary}${spreadReadout ? `, ${spreadReadout.pickLabel}, ${spreadReadout.result.toLowerCase()}` : pickStateLabel ? `, ${pickStateLabel.toLowerCase()}` : ''}`}
      >
        <Box style={styles.picksHeader}>
          <Box style={[styles.picksStatusChip, isCompleted && styles.picksStatusChipFinal]}>
            {!isCompleted ? <Box style={styles.picksStatusDot} /> : null}
            <Text style={[styles.picksStatusText, isCompleted && styles.picksStatusTextFinal]}>
              {isCompleted ? 'FINAL' : 'LIVE'}
            </Text>
          </Box>
          {!isCompleted && (period || clock) ? (
            <Box style={styles.picksClockChip}>
              <Text style={styles.picksClockText}>{[period, clock].filter(Boolean).join(' · ')}</Text>
            </Box>
          ) : null}
          {network ? <Text style={styles.picksNetwork}>{network}</Text> : null}
        </Box>
        <Box style={styles.picksScoreRow}>
          <PicksScore label={awayLabel} score={awayScore} isTrailing={homeIsWinning} />
          <Box style={styles.picksDivider} />
          <PicksScore label={homeLabel} score={homeScore} isTrailing={awayIsWinning} />
        </Box>
        {!isCompleted && situation ? (
          <Box style={styles.picksSituation}>
            <Text style={styles.picksSituationText}>{situation}</Text>
          </Box>
        ) : null}
        {spreadReadout ? (
          <Box style={[
            styles.spreadImpact,
            isCompactSpread && styles.spreadImpactCompact,
            spreadReadout.tone === 'positive' ? styles.spreadImpactPositive
              : spreadReadout.tone === 'neutral' ? styles.spreadImpactNeutral : undefined,
          ]}>
            <Box style={[styles.spreadImpactTop, isCompactSpread && styles.spreadImpactTopCompact]}>
              <Text style={styles.spreadImpactPick}>{spreadReadout.pickLabel}</Text>
              <Box style={styles.spreadImpactReadout}>
                <Text style={[
                  styles.spreadImpactHeadline,
                  spreadReadout.tone === 'positive' ? styles.spreadImpactHeadlinePositive
                    : spreadReadout.tone === 'neutral' ? styles.spreadImpactHeadlineNeutral : undefined,
                ]}>{spreadReadout.headline}</Text>
                <Text style={styles.spreadImpactSubhead}>{spreadReadout.subhead}</Text>
                {!isCompactSpread ? (
                  <Text style={styles.spreadImpactDetail}>{spreadReadout.fullDetail}</Text>
                ) : null}
              </Box>
            </Box>
            <Box
              style={[styles.spreadBars, isCompactSpread && styles.spreadBarsCompact]}
              accessible
              accessibilityLabel={[
                spreadReadout.fullDetail,
                spreadReadout.subhead,
                spreadBars?.away.spreadBoost
                  ? `${spreadBars.away.label} starts with a ${formatSigned(spreadBars.away.spreadBoost)} spread bar`
                  : null,
                spreadBars?.home.spreadBoost
                  ? `${spreadBars.home.label} starts with a ${formatSigned(spreadBars.home.spreadBoost)} spread bar`
                  : null,
              ].filter(Boolean).join('. ')}
            >
              {spreadBars ? (
                <>
                  <SpreadScoreBarRow
                    bar={spreadBars.away}
                    scale={spreadBars.scale}
                    color={awayBarColor}
                    result={spreadBars.result}
                  />
                  <SpreadScoreBarRow
                    bar={spreadBars.home}
                    scale={spreadBars.scale}
                    color={homeBarColor}
                    result={spreadBars.result}
                  />
                  <SpreadScoreAxis
                    away={spreadBars.away}
                    home={spreadBars.home}
                    scale={spreadBars.scale}
                  />
                </>
              ) : null}
            </Box>
          </Box>
        ) : pickStateLabel && PickStateIcon ? (
          <Box style={styles.picksResult}>
            <PickStateIcon size={15} color={pickState === 'correct' ? tokens.colors.state.success : tokens.colors.state.error} />
            <Text style={styles.picksResultText}>{pickStateLabel} PICK</Text>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      style={styles.scoreboard}
      accessibilityLabel={`Game scoreboard${pickStateLabel ? `, ${pickStateLabel.toLowerCase()}` : ''}`}
    >
      <Box style={styles.header}>
        <Box style={[styles.headerSide, styles.headerSideAway]}>
          <Text style={styles.headerLabel}>{awayLabel ?? 'GUEST'}</Text>
        </Box>
        <Box style={styles.headerSide}>
          <Text style={styles.headerLabel}>{homeLabel ?? 'HOME'}</Text>
        </Box>
      </Box>
      <Box style={styles.scoreRow}>
        <Box style={styles.team}>
          <LedScore score={awayScore} isWinning={awayIsWinning} />
        </Box>
        <Text style={styles.centerMark}>:</Text>
        <Box style={styles.team}>
          <LedScore score={homeScore} isWinning={homeIsWinning} />
        </Box>
      </Box>
      {hasPeriodScores ? (
        <Box style={styles.stats} accessibilityLabel="Quarter scores">
          {periodLabels.map((label, index) => (
            <Box
              key={label}
              style={[
                styles.statCell,
                index < periodLabels.length - 1 ? styles.statCellDivider : undefined,
              ]}
            >
              <Text style={styles.statLabel}>{label}</Text>
              <Box style={styles.statPair}>
                <Text style={styles.statValue}>{awayPeriods[index] ?? '—'}</Text>
                <Text style={styles.statSeparator}>|</Text>
                <Text style={styles.statValue}>{homePeriods[index] ?? '—'}</Text>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
      {spreadReadout ? (
        <Box style={styles.spreadDetail} accessibilityLabel={`${spreadReadout.pickLabel}, ${spreadReadout.result.toLowerCase()}`}>
          <Text style={styles.spreadPickLabel}>{spreadReadout.pickLabel}</Text>
          <Text style={[
            styles.spreadResult,
            spreadReadout.tone === 'positive' ? styles.spreadResultPositive
              : spreadReadout.tone === 'negative' ? styles.spreadResultNegative : styles.spreadResultNeutral,
          ]}>{spreadReadout.result}</Text>
        </Box>
      ) : null}
      <Box style={styles.footer}>
        <Text style={styles.status}>{isCompleted ? 'FINAL' : 'LIVE'}</Text>
        {pickStateLabel && PickStateIcon && !spreadReadout ? (
          <>
            <Box style={styles.footerDivider} />
            <Box style={styles.pickState}>
              <PickStateIcon
                size={14}
                color={pickState === 'correct' ? scoreboardSuccess : scoreboardFailure}
              />
              <Text
                style={[
                  styles.pickStateLabel,
                  pickState === 'correct' ? styles.pickStateLabelCorrect : styles.pickStateLabelIncorrect,
                ]}
              >
                {pickStateLabel}
              </Text>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
});
